import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, qs, useAction, useApi } from '../lib/api';
import { acct, addPeriods, date, period, titleCase, todayIso } from '../lib/format';
import { useAccounts } from '../lib/lookups';
import { useSession } from '../lib/session';
import type { Account, GeneralLedger, TrialBalance } from '../lib/types';
import { Icons } from '../components/icons';
import { Badge, Button, Card, Field, Input, Modal, PageHeader, Query, Select, SourceBadge, Stat, Table, Td, Th, Toggle, Tr } from '../components/ui';

const TYPES = ['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'] as const;
const TYPE_LABEL: Record<string, string> = { ASSET: 'Assets', LIABILITY: 'Liabilities', EQUITY: 'Equity', INCOME: 'Income', EXPENSE: 'Expenses' };
const debitNormal = (t: string) => t === 'ASSET' || t === 'EXPENSE';

export function AccountsPage() {
  const navigate = useNavigate();
  const { currency, can } = useSession();
  const [showInactive, setShowInactive] = useState(false);
  const [creating, setCreating] = useState(false);
  const accounts = useApi<{ accounts: Account[]; subtypes: Record<string, string[]> }>(`/api/accounts${qs({ includeInactive: showInactive || undefined })}`);
  const thisPeriod = todayIso().slice(0, 7);
  const tb = useApi<TrialBalance>(`/api/reports/trial-balance?period=${thisPeriod}`);
  const bal = new Map((tb.data?.rows ?? []).map((r) => [r.accountId, r.debit - r.credit]));

  return (
    <>
      <PageHeader
        title="Chart of accounts"
        description={`Balances are consolidated in ${currency} as of the end of ${period(thisPeriod)}. Income and expense balances are fiscal year to date.`}
        actions={
          <>
            <label className="flex items-center gap-2 text-[12.5px] text-muted">
              <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> Show inactive
            </label>
            {can('ADMIN') && (
              <Button variant="primary" icon={<Icons.Plus size={15} />} onClick={() => setCreating(true)}>
                New account
              </Button>
            )}
          </>
        }
      />
      <Query q={accounts}>
        {(d) => (
          <div className="space-y-4">
            {TYPES.map((t) => {
              const list = d.accounts.filter((a) => a.type === t);
              const total = list.reduce((s, a) => s + (bal.get(a.id) ?? 0), 0);
              return (
                <Card key={t} pad={false} title={TYPE_LABEL[t]} actions={<span className="num text-[13px] font-semibold">{acct(debitNormal(t) ? total : -total, currency)}</span>}>
                  <Table dense>
                    <thead>
                      <tr>
                        <Th w="90px">Code</Th>
                        <Th>Name</Th>
                        <Th>Subtype</Th>
                        <Th>Role</Th>
                        <Th right w="180px">
                          Balance
                        </Th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.map((a) => {
                        const b = bal.get(a.id) ?? 0;
                        return (
                          <Tr key={a.id} onClick={() => navigate(`/accounts/${a.id}`)}>
                            <Td className="num text-muted">{a.code}</Td>
                            <Td>
                              <span className={a.isActive ? '' : 'text-faint line-through'}>{a.name}</span>
                              {a.intercompany && (
                                <Badge tone="info" className="ml-2">
                                  Intercompany
                                </Badge>
                              )}
                            </Td>
                            <Td muted className="text-[12.5px]">
                              {titleCase(a.subtype)}
                            </Td>
                            <Td muted className="text-[12px]">
                              {a.systemRole ? titleCase(a.systemRole) : ''}
                            </Td>
                            <Td right>{acct(debitNormal(t) ? b : -b, currency)}</Td>
                          </Tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </Card>
              );
            })}
            <NewAccountModal open={creating} onClose={() => setCreating(false)} subtypes={d.subtypes} />
          </div>
        )}
      </Query>
    </>
  );
}

function NewAccountModal({ open, onClose, subtypes }: { open: boolean; onClose: () => void; subtypes: Record<string, string[]> }) {
  const [f, setF] = useState({ code: '', name: '', type: 'EXPENSE', subtype: '', cashFlowCategory: '', intercompany: false });
  const create = useAction(() => api('/api/accounts', { body: { ...f, subtype: f.subtype || subtypes[f.type]?.[0], cashFlowCategory: f.cashFlowCategory || undefined } }), { success: 'Account created' });
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New account"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={create.isPending} disabled={!f.code || !f.name} onClick={() => create.mutate(undefined, { onSuccess: onClose })}>
            Create account
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-[110px_minmax(0,1fr)] gap-3">
        <Field label="Code">
          <Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} placeholder="6450" />
        </Field>
        <Field label="Name">
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Conferences" />
        </Field>
        <Field label="Type">
          <Select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value, subtype: '' })}>
            {TYPES.map((t) => (
              <option key={t} value={t}>
                {titleCase(t)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Subtype">
          <Select value={f.subtype} onChange={(e) => setF({ ...f, subtype: e.target.value })}>
            {(subtypes[f.type] ?? []).map((s) => (
              <option key={s} value={s}>
                {titleCase(s)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Cash flow" className="col-span-2" hint="Where movements in this account land on the indirect cash flow statement">
          <Select value={f.cashFlowCategory} onChange={(e) => setF({ ...f, cashFlowCategory: e.target.value })}>
            <option value="">Default for the type</option>
            {['OPERATING', 'INVESTING', 'FINANCING', 'CASH'].map((c) => (
              <option key={c} value={c}>
                {titleCase(c)}
              </option>
            ))}
          </Select>
        </Field>
        <div className="col-span-2 rounded-md border border-line p-3">
          <Toggle checked={f.intercompany} onChange={(v) => setF({ ...f, intercompany: v })} label="Intercompany" description="Eliminated in consolidated reports" />
        </div>
      </div>
      {create.error && <p className="mt-3 text-[12.5px] text-bad">{create.error.message}</p>}
    </Modal>
  );
}

export function AccountLedgerPage() {
  const { id } = useParams();
  const { currency } = useSession();
  const { byId } = useAccounts();
  const a = byId.get(id!);
  const today = todayIso();
  const [from, setFrom] = useState(`${addPeriods(today.slice(0, 7), -2)}-01`);
  const [to, setTo] = useState(today);
  const q = useApi<GeneralLedger>(`/api/reports/general-ledger${qs({ accountId: id, fromDate: from, toDate: to })}`);
  const sign = a && !debitNormal(a.type) ? -1 : 1;

  return (
    <>
      <PageHeader
        eyebrow={
          <Link to="/accounts" className="hover:text-ink">
            ← Chart of accounts
          </Link>
        }
        title={a ? `${a.code} · ${a.name}` : 'Account'}
        description={a ? `${titleCase(a.type)} · ${titleCase(a.subtype)}${a.systemRole ? ` · system role ${titleCase(a.systemRole)}` : ''}` : undefined}
        actions={
          <div className="flex items-center gap-2">
            <Input type="date" className="w-36" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="From" />
            <span className="text-muted">–</span>
            <Input type="date" className="w-36" value={to} onChange={(e) => setTo(e.target.value)} aria-label="To" />
          </div>
        }
      />
      <Query q={q}>
        {(g) => {
          const dr = g.lines.reduce((s, l) => s + l.debit, 0);
          const cr = g.lines.reduce((s, l) => s + l.credit, 0);
          return (
            <>
              <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
                <Stat label={`Opening · ${date(from)}`} value={acct(g.openingBalance * sign, currency)} />
                <Stat label="Debits" value={acct(dr, currency)} sub={`${g.lines.filter((l) => l.debit).length} lines`} />
                <Stat label="Credits" value={acct(cr, currency)} sub={`${g.lines.filter((l) => l.credit).length} lines`} />
                <Stat label={`Closing · ${date(to)}`} value={acct(g.closingBalance * sign, currency)} />
              </div>
              <Card pad={false}>
                <Table dense>
                  <thead>
                    <tr>
                      <Th w="105px">Date</Th>
                      <Th w="70px">Entry</Th>
                      <Th>Description</Th>
                      <Th>Counterparty</Th>
                      <Th>Channel</Th>
                      <Th right>Debit</Th>
                      <Th right>Credit</Th>
                      <Th right>Balance</Th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="bg-paper/60">
                      <Td colSpan={7} muted>
                        Opening balance
                      </Td>
                      <Td right className="font-medium">
                        {acct(g.openingBalance * sign, currency)}
                      </Td>
                    </tr>
                    {g.lines.map((l) => (
                      <tr key={l.id}>
                        <Td className="num whitespace-nowrap">{date(l.date)}</Td>
                        <Td>
                          <Link to={`/journal/${l.entryId}`} className="num text-brand hover:underline">
                            #{l.number}
                          </Link>
                        </Td>
                        <Td className="max-w-[340px]">
                          <div className="truncate">{l.description || l.memo}</div>
                          {l.currency !== currency && (
                            <div className="num text-[11.5px] text-muted">
                              {acct(l.txnDebit || -l.txnCredit, l.currency)} {l.currency}
                            </div>
                          )}
                        </Td>
                        <Td muted className="max-w-[180px] truncate">
                          {l.counterparty}
                        </Td>
                        <Td>
                          <SourceBadge source={l.submissionSource} />
                        </Td>
                        <Td right>{l.debit ? acct(l.debit, currency) : ''}</Td>
                        <Td right>{l.credit ? acct(l.credit, currency) : ''}</Td>
                        <Td right className="font-medium">
                          {acct(l.balance * sign, currency)}
                        </Td>
                      </tr>
                    ))}
                    {!g.lines.length && (
                      <tr>
                        <Td colSpan={8} muted className="py-8 text-center">
                          No activity in this range
                        </Td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Card>
            </>
          );
        }}
      </Query>
    </>
  );
}
